/**
 * Retour afgerond buiten het portaal (sessie 186, besluit JP 20-10, keuze A).
 *
 * Eén paneel voor het dossier (onder Meer) en de snelle actie op een rij
 * in de retourlijst. Eerst een reden kiezen (bij "Anders" een korte
 * tekst), dan een eigen bevestigingsstap in de pagina. Nooit
 * window.confirm. Het gevolg: status afgerond, een regel in het logboek;
 * geen mail aan de klant, geen label en geen geld via het portaal.
 */
import { useMutation } from "@tanstack/react-query";
import { useId, useState } from "react";

import { vertaalApiFout } from "@/lib/api";
import type { RetourV3Dossier } from "@/lib/retouren-v3-api";

import {
  BUITEN_PORTAAL_REDENEN,
  rondAfBuitenPortaal,
  TOELICHTING_MAX,
  type BuitenPortaalReden,
} from "./buitenPortaal";
import "./dossier-v3.css";

interface Props {
  retourId: string;
  retournummer?: string | null;
  onKlaar: (dossier: RetourV3Dossier) => void;
  onSluiten: () => void;
}

export function AfrondenBuitenPortaal({ retourId, retournummer, onKlaar, onSluiten }: Props) {
  const naam = useId();
  const [reden, setReden] = useState<BuitenPortaalReden | null>(null);
  const [tekst, setTekst] = useState("");
  const [bevestigen, setBevestigen] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  const schoon = tekst.trim();
  const geldig = reden !== null && (reden !== "anders" || schoon.length > 0);
  const redenZin =
    reden === "anders"
      ? `anders, ${schoon}`
      : (BUITEN_PORTAAL_REDENEN.find((r) => r.sleutel === reden)?.label.toLowerCase() ?? "");

  const afronden = useMutation({
    mutationFn: () =>
      rondAfBuitenPortaal(retourId, {
        reden: reden as BuitenPortaalReden,
        toelichting: reden === "anders" ? schoon : null,
      }),
    onSuccess: (nieuw) => onKlaar(nieuw),
    onError: (e) => {
      setBevestigen(false);
      setFout(vertaalApiFout(e, "Afronden is niet gelukt. Er is niets gewijzigd."));
    },
  });

  if (bevestigen && geldig) {
    return (
      <div className="rv3d-paneel" data-testid="rv3d-buitenportaal-bevestig">
        <p>
          <strong>
            {retournummer ? `Retour ${retournummer}` : "Deze retour"} afronden buiten het
            portaal?
          </strong>
        </p>
        <p>Reden: {redenZin}.</p>
        <p className="rv3d-melding">
          De klant krijgt geen mail, er komt geen label en er gaat geen geld via het portaal.
          Herinneringen stoppen.
        </p>
        <div className="rv3d-paneel-knoppen">
          <button
            type="button"
            className="rv3d-hoofdknop"
            data-testid="rv3d-buitenportaal-ja"
            disabled={afronden.isPending}
            onClick={() => afronden.mutate()}
          >
            {afronden.isPending ? "Bezig…" : "Ja, rond af"}
          </button>
          <button
            type="button"
            className="rv3d-knop rv3d-knop-klein"
            disabled={afronden.isPending}
            onClick={() => setBevestigen(false)}
          >
            Terug
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="rv3d-paneel"
      data-testid="rv3d-buitenportaal"
      onSubmit={(e) => {
        e.preventDefault();
        if (!geldig) return;
        setFout(null);
        setBevestigen(true);
      }}
    >
      <fieldset className="rv3d-buitenportaal-redenen">
        <legend className="rv3d-veldlabel">Waarom is deze retour al afgerond?</legend>
        {BUITEN_PORTAAL_REDENEN.map((r) => (
          <label key={r.sleutel} className="rv3d-buitenportaal-reden">
            <input
              type="radio"
              name={naam}
              value={r.sleutel}
              checked={reden === r.sleutel}
              onChange={() => setReden(r.sleutel)}
            />
            {r.label}
          </label>
        ))}
      </fieldset>
      {reden === "anders" ? (
        <label className="rv3d-veldlabel">
          Korte reden
          <textarea
            className="rv3d-veld"
            rows={2}
            maxLength={TOELICHTING_MAX}
            data-testid="rv3d-buitenportaal-tekst"
            value={tekst}
            onChange={(e) => setTekst(e.target.value)}
          />
        </label>
      ) : null}
      {fout ? (
        <div className="rv3d-fout" role="alert">
          {fout}
        </div>
      ) : null}
      <div className="rv3d-paneel-knoppen">
        <button
          type="submit"
          className="rv3d-knop rv3d-knop-klein"
          data-testid="rv3d-buitenportaal-verder"
          disabled={!geldig}
        >
          Verder
        </button>
        <button type="button" className="rv3d-knop rv3d-knop-klein" onClick={onSluiten}>
          Annuleren
        </button>
      </div>
    </form>
  );
}
